'use client'
import { useEffect, useRef, useState } from 'react'
import { EXPERIMENT_RESOLVE_TIMEOUT_MS } from '@/utils/experiments'

export const CONTROL_VARIANT = 'control'

export interface UseExperimentResult {
  /** Resolved variant key; `'control'` until (and unless) the flag resolves in time. */
  variant: string
  /** True once the variant is final. Keep the tested element hidden until then. */
  ready: boolean
}

/**
 * Resolves a PostHog experiment variant with an anti-flicker cap
 * (MAR-5415). Returns control immediately when no flag key is given, when
 * PostHog isn't running (dev builds, consent-blocked), or when the flag
 * hasn't resolved within EXPERIMENT_RESOLVE_TIMEOUT_MS.
 *
 * Once the timeout locks in control, a late flag response is ignored and
 * getFeatureFlag is never called for it — so no `$feature_flag_called`
 * exposure is recorded and the visitor stays out of the experiment
 * instead of polluting it with a variant they never saw.
 */
interface ExperimentState extends UseExperimentResult {
  /** Flag key this state belongs to; guards against returning a previous key's result. */
  key: string | undefined
}

export default function useExperiment(flagKey: string | undefined): UseExperimentResult {
  const [state, setState] = useState<ExperimentState>({ key: flagKey, variant: CONTROL_VARIANT, ready: !flagKey })
  const doneRef = useRef(false)

  // Render-phase reset when the key changes (including removal) so a
  // previous key's resolved variant is never exposed for the new one.
  if (state.key !== flagKey) {
    setState({ key: flagKey, variant: CONTROL_VARIANT, ready: !flagKey })
  }

  useEffect(() => {
    if (!flagKey) return

    doneRef.current = false
    let cancelled = false
    let unsubscribe: (() => void) | undefined

    const finalize = (variant: string) => {
      if (doneRef.current || cancelled) return
      doneRef.current = true
      setState({ key: flagKey, variant, ready: true })
    }

    const timeoutId = setTimeout(() => finalize(CONTROL_VARIANT), EXPERIMENT_RESOLVE_TIMEOUT_MS)

    // posthog-js stays dynamically imported (bundle: ~189KB); on experiment
    // pages CSPostHogProvider inits it eagerly, so flags are usually already
    // in flight. onFeatureFlags fires immediately when flags are loaded.
    import('posthog-js')
      .then(({ default: posthog }) => {
        if (cancelled || doneRef.current) return
        unsubscribe = posthog.onFeatureFlags(() => {
          if (doneRef.current) return
          const value = posthog.getFeatureFlag(flagKey)
          finalize(typeof value === 'string' ? value : CONTROL_VARIANT)
        })
      })
      .catch(() => finalize(CONTROL_VARIANT))

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      unsubscribe?.()
    }
  }, [flagKey])

  if (state.key !== flagKey) {
    return { variant: CONTROL_VARIANT, ready: !flagKey }
  }
  return { variant: state.variant, ready: state.ready }
}
